import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import SponsoredRateCard from "./SponsoredRateCard";
import { adminSponsored, adminSponsoredCreate, adminSponsoredCancel, adminSponsoredPricing } from "../lib/api";

export default function AdminSponsored() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ slug: "", placement: "", months: 3, price_usd: 0, note: "" });
  const [tiers, setTiers] = useState({});
  const load = useCallback(async () => {
    try {
      const d = await adminSponsored();
      setData(d);
      setTiers(Object.fromEntries(Object.entries(d.pricing.tiers).map(([k, v]) => [k, String(v)])));
    } catch {
      toast.error("could not load sponsored slots");
    }
  }, []);
  useEffect(() => {
    Promise.resolve().then(load);
  }, [load]);

  const create = async () => {
    try {
      await adminSponsoredCreate({ ...form, months: Number(form.months), price_usd: Number(form.price_usd), note: form.note || null });
      toast.success("slot assigned");
      setForm((f) => ({ ...f, slug: "", note: "" }));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "failed");
    }
  };
  const cancel = async (id) => {
    if (!window.confirm("cancel this sponsorship?")) return;
    await adminSponsoredCancel(id);
    load();
  };
  const savePrice = async () => {
    const t = Object.fromEntries(Object.entries(tiers).map(([k, v]) => [k, Number(v)]));
    if (Object.values(t).some((v) => !v || v < 1)) return toast.error("every tier needs a price of at least $1");
    try {
      await adminSponsoredPricing({ tiers: t, overrides: data.pricing.overrides || {} });
      toast.success("tier prices saved");
      load();
    } catch {
      toast.error("failed");
    }
  };
  const usd = (n) => `$${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

  if (!data) return null;
  const active = data.sponsorships.filter((s) => s.status === "active");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const inp = "border border-black p-1 text-[12px] font-mono";
  return (
    <section data-testid="admin-sponsored">
      <h2 className="text-[15px] font-bold border-b border-black pb-1">sponsored slots ({active.length} active)</h2>
      <p className="text-[#444] mt-2">Labelled rows above the ranked list on the homepage (max {data.home_max}), /best/* and /alternatives/* pages (max {data.max_per_placement}). Ranking is never affected. Only visible to the public when the <b>sponsored</b> switch is on.</p>

      <div className="mt-3 border border-black p-2" data-testid="sponsored-pricing">
        <div className="font-bold mb-1">price per month, by tier · {data.pricing.min_months}-month minimum · 12 months billed as {data.pricing.annual_months_charged}</div>
        <div className="flex flex-wrap gap-x-5 gap-y-2 items-end">
          {["home", "category", "standard", "alternatives"].map((k) => (
            <label key={k} className="flex flex-col text-[11px] text-[#666]">
              {data.pricing.tier_labels[k]}
              <span className="text-black text-[12px]">$ <input value={tiers[k] ?? ""} onChange={(e) => setTiers((t) => ({ ...t, [k]: e.target.value }))} className={`${inp} w-20`} data-testid={`sponsored-tier-${k}`} /></span>
            </label>
          ))}
          <button type="button" className="tv-btn" onClick={savePrice} data-testid="sponsored-price-save">[save tiers]</button>
        </div>
        <div className="mt-2 text-[#444]" data-testid="sponsored-max-revenue">
          <b>max revenue if every slot sold: {usd(data.inventory.max_month_usd)}/month · {usd(data.inventory.max_annual_usd)}/year</b>
          <span className="text-[#666]"> at monthly rates ({usd(data.inventory.max_annual_prepaid_usd)}/year if everyone prepays 12 months) — {data.inventory.slots} slots:{" "}
            {data.inventory.by_tier.map((t) => `${t.pages} ${t.label}${t.pages === 1 ? "" : "s"} × ${t.slots / t.pages} = ${usd(t.month_usd)}/mo`).join(" · ")}
          </span>
        </div>
      </div>

      <SponsoredRateCard key={JSON.stringify(data.pricing.tiers)} onSaved={load} />

      <div className="mt-3 border border-black p-2" data-testid="sponsored-create">
        <div className="font-bold mb-1">assign a slot manually (sold offline / comp)</div>
        <div className="flex flex-wrap gap-2 items-center">
          <input placeholder="tool slug" value={form.slug} onChange={set("slug")} className={`${inp} w-40`} data-testid="sponsored-form-slug" />
          <select value={form.placement} onChange={set("placement")} className={`${inp} max-w-[320px]`} data-testid="sponsored-form-placement">
            <option value="">page…</option>
            {data.placements.map((p) => <option key={p.placement} value={p.placement}>{p.label}</option>)}
          </select>
          <input placeholder="or alternatives:<slug>" value={form.placement.startsWith("alternatives:") ? form.placement : ""} onChange={set("placement")} className={`${inp} w-44`} data-testid="sponsored-form-alt" />
          <input type="number" min={1} max={24} value={form.months} onChange={set("months")} className={`${inp} w-16`} title="months" data-testid="sponsored-form-months" />
          <span>months · total $</span>
          <input type="number" min={0} value={form.price_usd} onChange={set("price_usd")} className={`${inp} w-20`} title="total paid (0 = comp)" data-testid="sponsored-form-price" />
          <input placeholder="note" value={form.note} onChange={set("note")} className={`${inp} w-40`} data-testid="sponsored-form-note" />
          <button type="button" className="tv-btn tv-btn-primary" disabled={!form.slug || !form.placement} onClick={create} data-testid="sponsored-form-submit">[assign]</button>
        </div>
      </div>

      <ul className="mt-3 space-y-1" data-testid="sponsored-list">
        {data.sponsorships.slice(0, 100).map((s) => (
          <li key={s.id} className="flex flex-wrap gap-x-3" data-testid={`sponsored-row-${s.id}`}>
            <span className={s.status === "active" ? "font-bold" : "text-[#888]"}>{s.status}</span>
            <a href={`/tool/${s.slug}`} className="tv-link" target="_blank" rel="noreferrer">{s.slug}</a>
            <span>{s.placement_label}</span>
            <span className="text-[#666]">{String(s.starts_at).slice(0, 10)} → {String(s.ends_at).slice(0, 10)} · ${s.price_usd} · {s.source}{s.note ? ` · ${s.note}` : ""}</span>
            {s.status === "active" && <button type="button" className="tv-link underline text-[#b91c1c]" onClick={() => cancel(s.id)} data-testid={`sponsored-cancel-${s.id}`}>cancel</button>}
          </li>
        ))}
      </ul>
    </section>
  );
}
